/**
 * Scripted test players — FOR PIPELINE TESTING ONLY.
 *
 * These drive the same input path as a human (raw commands through the
 * control-latency FIFO) so classification and telemetry can be validated
 * end-to-end. Episodes produced with a scripted player are hard-flagged
 * player_type: "scripted" and must NEVER be pooled with human research data.
 */

import { Rng, deriveSeed } from "../core/rng";
import { clamp, wrapAngle } from "../core/vec";
import type { JoystickCommand, Side, SimView } from "../sim/types";

export type PlayerFn = (view: SimView, t: number) => JoystickCommand;

export interface ScriptedPlayerDef {
  name: string;
  create(seed: number): PlayerFn;
}

const FULL = 1;

/**
 * Aim the robot at a world bearing in whichever control model the level uses
 * (ADR-023). Heading model: the stick points where to go, relative to the
 * control frame. Rate model: the stick is a wheel, so hold it over in
 * proportion to the heading error — what a driver does.
 */
function steerToward(view: SimView, bearing: number, throttle = FULL): JoystickCommand {
  if (view.controlScheme === "rate") {
    const err = wrapAngle(bearing - view.robot.heading);
    // Proportional on the heading error, damped by the yaw already happening:
    // without the damping term a rate-steered vehicle weaves.
    const wheel = clamp(err * 1.9 - view.robot.yawRate * 0.55, -1, 1);
    // Ease off the throttle the harder the wheel is over, like a driver.
    const drive = throttle * (1 - 0.45 * Math.min(1, Math.abs(wheel)));
    const mag = Math.min(1, Math.hypot(wheel, drive));
    return mag < 1e-6 ? { dirX: 0, dirY: 1, mag: throttle } : { dirX: wheel / mag, dirY: drive / mag, mag };
  }
  const rel = clamp(wrapAngle(bearing - view.frameYaw), -1.65, 1.65);
  return { dirX: Math.sin(rel), dirY: Math.cos(rel), mag: throttle };
}

/** Comfortable pass lane in the 2.2 m alley (robot half-width 0.3, walls at 1.1). */
const LANE_X = 0.55;

/** Steer toward a lateral lane while always driving forward; converge on the
 * destination marker once the encounter zone is behind. */
function laneCommand(view: SimView, laneX: number): JoystickCommand {
  const nearDest = view.robot.z >= view.destination.z - 4;
  const targetX = nearDest ? view.destination.x : laneX;
  const dx = clamp((targetX - view.robot.x) * 1.5, -0.85, 0.85);
  return { dirX: dx, dirY: 1, mag: FULL };
}

const alwaysLeft: ScriptedPlayerDef = {
  name: "always_left",
  create: () => (view) => laneCommand(view, -LANE_X),
};

const alwaysRight: ScriptedPlayerDef = {
  name: "always_right",
  create: () => (view) => laneCommand(view, LANE_X),
};

const alwaysYield: ScriptedPlayerDef = {
  name: "always_yield",
  create: () => (view) => {
    const ped = view.ped;
    const pedGone = !ped || !ped.active || ped.z < view.robot.z - 1;
    if (!pedGone) return { dirX: 0, dirY: 0, mag: 0 };
    return laneCommand(view, 0);
  },
};

/** Pass on the side opposite the pedestrian's drift (complementary maneuver). */
const followComplementary: ScriptedPlayerDef = {
  name: "follow_complementary",
  create: () => (view) => {
    if (!view.cueOnsetSeen) return laneCommand(view, 0);
    const side = view.pedDriftSide;
    // Variant C (no drift): deterministically default to the right lane.
    const targetX = side === "left" ? LANE_X : side === "right" ? -LANE_X : LANE_X;
    return laneCommand(view, targetX);
  },
};

const oscillate: ScriptedPlayerDef = {
  name: "oscillate",
  create: () => (_view, t) => ({
    dirX: 0.9 * Math.sin((2 * Math.PI * t) / 1.6),
    dirY: 0.8,
    mag: FULL,
  }),
};

const randomPlayer: ScriptedPlayerDef = {
  name: "random",
  create: (seed) => {
    const rng = new Rng(deriveSeed(seed, 7));
    let nextChangeT = 0;
    let cmd: JoystickCommand = { dirX: 0, dirY: 1, mag: FULL };
    return (_view, t) => {
      if (t >= nextChangeT) {
        nextChangeT = t + 0.5;
        const release = rng.next() < 0.15;
        cmd = release
          ? { dirX: 0, dirY: 0, mag: 0 }
          : { dirX: rng.range(-1, 1), dirY: rng.range(0.2, 1), mag: FULL };
      }
      return cmd;
    };
  },
};

/**
 * Recovery-demo players (dev/testing only, NOT part of the canonical six):
 * they deliberately grind the robot into a wall to exercise stuck detection.
 */
const wallRam: ScriptedPlayerDef = {
  name: "wall_ram",
  create: () => (view) => {
    // Ram nearly perpendicular into the right wall until one recovery has
    // happened, then drive on to the destination.
    if (view.recoveryCount < 1) return { dirX: 1, dirY: 0.05, mag: FULL };
    return laneCommand(view, 0);
  },
};

const wallRamTwice: ScriptedPlayerDef = {
  name: "wall_ram_twice",
  create: () => (view) => {
    // Keep ramming through the first recovery so a second, consecutive stuck
    // triggers the safe-pose restore; then drive on.
    if (view.recoveryCount < 2) return { dirX: 1, dirY: 0.05, mag: FULL };
    return laneCommand(view, 0);
  },
};

// ---------------------------------------------------------------------------
// Mission players (ADR-022): follow the delivery path through junctions and
// the roundabout, avoiding agents by picking the visibly wider side (or a
// preferred side / yielding at the research pedestrian). Commands are issued
// in the CONTROL FRAME (road-relative), exactly like a human's joystick.
// Test/pipeline use only, like all scripted players.

/** Half of Bulldog04's measured width (0.709 m), for kerb-relative lane keeping. */
const BODY_HALF_M = 0.355;

function makeMissionNav(
  researchPrefer: Side | null,
  yieldAtResearch: boolean,
  opts: {
    /**
     * Hold the robot's right side this far off the RIGHT kerb instead of
     * riding the path (ADR-085). Bulldog's operators hug the kerb at ~0.3 m;
     * a teacher that rides the lane centre teaches a student the wrong
     * lateral habit, which showed up as a +0.12 rad/s leftward bias on real
     * footage (SIM_DATA_EXPERIMENT_VI.md §5).
     */
    kerbGapM?: number;
  } = {},
): PlayerFn {
  // Sticky pass-side (relative to each agent) so the player commits to one
  // side instead of dithering as the obstacle inches sideways.
  const chosenSide = new Map<string, 1 | -1>();
  // Boxed-in reflex: after 2 s at a standstill with an agent right in front,
  // pull back and pivot away from it for a moment (what a player does).
  let stillSince: number | null = null;
  let pivotUntil = -Infinity;
  let pivotDir: 1 | -1 = 1;
  let lastEngaged = false;

  const decide = (view: SimView, t: number): JoystickCommand => {
    const path = view.path;
    if (!path) return laneCommand(view, 0);
    const r = view.robot;
    const { s } = view.progress;

    if (t < pivotUntil) return { dirX: pivotDir * 0.6, dirY: -0.8, mag: FULL };
    // Only while TRYING to move (a deliberate yield is not being boxed in).
    if (lastEngaged && Math.abs(r.speed) < 0.1) {
      stillSince ??= t;
      if (t - stillSince > 2.0) {
        const fx = Math.sin(r.heading);
        const fz = Math.cos(r.heading);
        let blocker: { lat: number } | null = null;
        for (const a of view.agents) {
          const dx = a.x - r.x;
          const dz = a.z - r.z;
          const ahead = dx * fx + dz * fz;
          const lat = dx * fz - dz * fx;
          if (ahead > -0.2 && ahead < 2.0 && Math.abs(lat) < 1.4) blocker = { lat };
        }
        if (blocker) {
          pivotDir = blocker.lat > 0 ? -1 : 1;
          pivotUntil = t + 0.8;
          stillSince = null;
          return { dirX: pivotDir * 0.6, dirY: -0.8, mag: FULL };
        }
      }
    } else stillSince = null;

    // Look-ahead point on the path (farther when faster, shorter in bends so
    // the straight line to it never cuts the inside curb).
    let look = clamp(2.0 + r.speed * 0.55, 2.5, 7.0);
    const bend = Math.abs(wrapAngle(path.point(Math.min(s + look, path.length)).theta - path.point(s).theta));
    if (bend > 0.5) look = 3.0;
    const sT = Math.min(s + look, path.length);
    const h = path.point(sT).halfWidth - 0.6; // margin from the curbs
    // Consider agents within a closing-time horizon (~4 s), not a fixed distance.
    const horizon = Math.max(12, 4.0 * (r.speed + 1.5));

    // Static props as pseudo-agents: sample long boxes along their long axis.
    const props: Array<{ id: string; type: string; x: number; z: number; vx: number; vz: number; radius: number }> = [];
    for (const [i, ob] of view.obstacles.entries()) {
      // An UNMARKED pit is one no driver sees coming (ADR-043) — driving
      // into it unawares is the failure fall_in exists to model, so it is
      // invisible here. An ordinary pothole stays visible and avoided
      // (district_v2 has one on the route; cutting all pits from the scan
      // changed every trajectory past it and broke two validation runs).
      if (ob.pit && ob.unmarked) continue;
      if (Math.hypot(ob.x - r.x, ob.z - r.z) > 20) continue;
      const alongX = ob.halfW >= ob.halfD;
      const half = Math.max(ob.halfW, ob.halfD);
      const rad = Math.min(ob.halfW, ob.halfD);
      const n = Math.max(1, Math.ceil(half / 0.8));
      for (let k = 0; k < n; k++) {
        const u = n === 1 ? 0 : -half + rad + ((2 * (half - rad)) * k) / (n - 1);
        props.push({
          id: `prop${i}`,
          type: ob.kind ?? "prop",
          x: ob.x + (alongX ? u : 0),
          z: ob.z + (alongX ? 0 : u),
          vx: 0,
          vz: 0,
          radius: rad * 1.15,
        });
      }
    }

    // Agents (and props) that constrain the next meters, in path coordinates, nearest first.
    const relevant = [...view.agents, ...props]
      .map((a) => {
        const f = path.project(a.x, a.z, s, 20);
        const tangentX = Math.sin(f.theta);
        const tangentZ = Math.cos(f.theta);
        const along = a.vx * tangentX + a.vz * tangentZ;
        return { a, ds: f.s - s, d: f.d, along };
      })
      .filter(({ ds, d }) => ds > -1 && ds < horizon && Math.abs(d) < h + 1.5)
      .sort((p, q) => p.ds - q.ds);

    // Default lane position. The plain runner holds the path; the kerb runner
    // holds its right side `kerbGapM` off the right kerb, measured from the
    // kerb itself (the path may already sit off-centre in its lane, so the
    // half-width is no guide). March the path normal until the road ends.
    let baseD = 0;
    if (opts.kerbGapM !== undefined && view.network) {
      const net = view.network;
      const reach = path.point(sT).halfWidth + 3;
      let kerb = 0;
      while (kerb < reach) {
        const q = path.offsetPoint(sT, kerb + 0.1);
        if (net.sdf(q.x, q.z) > 0) break;
        kerb += 0.1;
      }
      baseD = clamp(kerb - BODY_HALF_M - opts.kerbGapM, -h, h);
    }
    let targetD = baseD;
    let yielding = false;

    // Corridor compliance (ADR-065 amendment F): the cap no longer pins the
    // robot at its pocket — waiting became the player's own move, so the
    // scripted 'careful player' models the taught behavior: roll the last
    // metres into the bay at walking pace, then stand still while the amber
    // holds. (Corridor-off runs skip this: view.corridor is null.)
    const cor = view.corridor;
    if (
      cor?.amber &&
      cor.pocket &&
      (cor.state === "WAITING_POCKET" || cor.state === "BLOCKED_RIGHT")
    ) {
      const pk = cor.pocket;
      if (s >= pk.s0 - 0.2) return { dirX: 0, dirY: 0, mag: 0 };
      if (s > pk.s0 - 4) {
        if (r.speed > 1.4) return { dirX: 0, dirY: 0, mag: 0 };
        const mid = clamp((pk.lo + pk.hi) / 2, -h, h);
        const tp = path.offsetPoint(Math.min(pk.s1 - 0.8, s + 2.5), mid);
        const cmd = steerToward(view, Math.atan2(tp.x - r.x, tp.z - r.z));
        return { ...cmd, mag: Math.min(cmd.mag, 0.45) };
      }
    }

    // Same-direction traffic ahead is FOLLOWED, not steered around: ease off
    // (release = brake) when closing on it, like a careful rider would.
    const followed = relevant.find(
      ({ ds, d, along }) => along >= 2.5 && ds > 0 && Math.abs(d) < 1.0 && ds < 3 + r.speed * 0.8,
    );
    if (followed && r.speed > followed.along - 0.5) return { dirX: 0, dirY: 0, mag: 0 };
    // Crossing traffic (fast, moving across our lane, arriving within ~3 s):
    // give way — ring traffic has priority, and a careful rider waits.
    const thetaNow = path.point(s).theta;
    const rx = Math.cos(thetaNow);
    const rz = -Math.sin(thetaNow);
    for (const { a, ds, d, along } of relevant) {
      const speed = Math.hypot(a.vx, a.vz);
      if (speed < 2.0 || Math.abs(along) > speed * 0.7) continue; // not crossing
      const latVel = a.vx * rx + a.vz * rz; // +right
      if (Math.abs(latVel) < 1.0) continue;
      const tCross = -d / latVel; // time until it reaches our lane line
      const ours = ds / Math.max(r.speed, 1.0);
      if (tCross > -0.3 && tCross < 3.0 && ds > -1 && ds < 4 + r.speed * 1.5 && Math.abs(tCross - ours) < 2.2) {
        return { dirX: 0, dirY: 0, mag: 0 };
      }
    }
    const steerable = relevant.filter(({ along }) => along < 2.5);
    relevant.length = 0;
    relevant.push(...steerable);

    if (relevant.length > 0) {
      const pressing = relevant[0]!;
      if (pressing.a.type === "research_ped" && yieldAtResearch && pressing.ds > -0.5) {
        return { dirX: 0, dirY: 0, mag: 0 };
      }

      if (pressing.a.type === "research_ped") {
        // Lab-like pass: commit to a lane on the chosen side and hold it —
        // after the commitment the pedestrian reacts and clears that side.
        let side = chosenSide.get(pressing.a.id);
        if (side === undefined) {
          side = researchPrefer
            ? researchPrefer === "left"
              ? -1
              : 1
            : pressing.d <= 0
              ? 1
              : -1; // default: side away from the drift
          chosenSide.set(pressing.a.id, side);
        }
        targetD = clamp(side * 0.55, -h, h);
      } else {
        // Free lateral intervals: the corridor minus every agent's blocked band.
        const CLEAR = 0.62; // robot half-width + margin past an agent
        type Iv = [number, number];
        let free: Iv[] = [[-h, h]];
        for (const { a, d } of relevant) {
          const lo = d - (a.radius + CLEAR);
          const hi = d + (a.radius + CLEAR);
          const next: Iv[] = [];
          for (const [lo0, hi0] of free) {
            if (hi <= lo0 || lo >= hi0) next.push([lo0, hi0]);
            else {
              if (lo > lo0) next.push([lo0, lo]);
              if (hi < hi0) next.push([hi, hi0]);
            }
          }
          free = next;
        }
        free = free.filter(([lo, hi]) => hi - lo >= 0.1);

        if (free.length === 0) {
          yielding = true;
        } else {
          let side = chosenSide.get(pressing.a.id);
          const crossing = Math.abs(pressing.a.vx) + Math.abs(pressing.a.vz) > 0.2 && Math.abs(pressing.along) < 0.3;
          if (crossing && pressing.a.type !== "dog") {
            // Pass BEHIND a crosser: the side it is coming from.
            const rx = Math.cos(path.point(s).theta);
            const rz = -Math.sin(path.point(s).theta);
            const latVel = pressing.a.vx * rx + pressing.a.vz * rz;
            side = latVel > 0 ? -1 : 1;
          } else if (side === undefined) {
            side = pressing.d + h >= h - pressing.d ? -1 : 1; // wider side
          }
          chosenSide.set(pressing.a.id, side);
          // Trim every gap to the actual road surface. The corridor is
          // symmetric about a path that may hug one kerb, so half of it can
          // lie on the pavement — and the widest-gap rule then climbed the
          // kerb to use it (r04: cruising at x 6.9 on a road that ends at 5).
          const net = view.network;
          const onRoad = (d: number): boolean => {
            if (!net) return true;
            const p = path.offsetPoint(sT, clamp(d, -h, h));
            return net.sdf(p.x, p.z) < -0.4;
          };
          if (net) {
            const trimmed = free
              .map(([lo, hi]): Iv => {
                while (hi - lo >= 0.1 && !onRoad(hi)) hi -= 0.25;
                while (hi - lo >= 0.1 && !onRoad(lo)) lo += 0.25;
                return [lo, hi];
              })
              .filter(([lo, hi]) => hi - lo >= 0.1);
            // If trimming leaves nothing (wedged against a kerb mid-recovery)
            // fall back to the untrimmed gaps: creeping past with a wheel
            // near the kerb beats standing dead in the road forever.
            if (trimmed.length > 0) free = trimmed;
          }
          {
            const wide = free.filter(([lo, hi]) => hi - lo >= 0.95);
            const pool0 = wide.length > 0 ? wide : free;
            const onSide = pool0.filter(([lo, hi]) =>
              side! < 0 ? (lo + hi) / 2 < pressing.d : (lo + hi) / 2 > pressing.d,
            );
            const pool = onSide.length > 0 ? onSide : pool0;
            // Swerve JUST enough: aim at the point of the chosen gap nearest
            // the path (d = 0), padded off its edge — never the middle of
            // the widest gap, which is how the runner ended up cruising the
            // crown of the road past a whole parked row and staying there.
            let best = pool[0]!;
            let bestDist = Infinity;
            for (const iv of pool) {
              const nearest = clamp(baseD, iv[0], iv[1]);
              if (Math.abs(nearest - baseD) < bestDist) {
                best = iv;
                bestDist = Math.abs(nearest - baseD);
              }
            }
            targetD = clamp(clamp(baseD, best[0] + 0.28, best[1] - 0.28), -h, h);
          }
        }
      }
    }

    if (yielding) return { dirX: 0, dirY: 0, mag: 0 };
    // Converge on the target lane rather than lunging at it: aiming straight
    // at a lane 5 m away (post-recovery, back from a kerb ram) overshot past
    // the path into whatever was parked on the far side. Real drivers close
    // big lateral errors over distance.
    const dNow = view.progress.d;
    targetD = dNow + clamp(targetD - dNow, -1.8, 1.8);
    const tp = path.offsetPoint(sT, clamp(targetD, -h, h));
    const bearing = Math.atan2(tp.x - r.x, tp.z - r.z);
    // Facing well away from where we want to go: rotate in place toward it
    // (a pulled-back stick), judged from the robot's own heading like a
    // player watching the robot would; otherwise steer relative to the
    // control frame, within the command arc.
    const relHeading = wrapAngle(bearing - r.heading);
    if (Math.abs(relHeading) > (70 * Math.PI) / 180 && r.speed < 2.5) {
      // Rate model: full lock and off the throttle spins it round on the spot.
      return view.controlScheme === "rate"
        ? { dirX: relHeading > 0 ? 1 : -1, dirY: -0.2, mag: FULL }
        : { dirX: relHeading > 0 ? 0.6 : -0.6, dirY: -0.8, mag: FULL };
    }
    return steerToward(view, bearing);
  };

  return (view, t) => {
    const cmd = decide(view, t);
    lastEngaged = cmd.mag > 0;
    return cmd;
  };
}

const missionRunner: ScriptedPlayerDef = {
  name: "mission_runner",
  create: () => makeMissionNav(null, false),
};

const missionLeft: ScriptedPlayerDef = {
  name: "mission_left",
  create: () => makeMissionNav("left", false),
};

const missionRight: ScriptedPlayerDef = {
  name: "mission_right",
  create: () => makeMissionNav("right", false),
};

const missionYielder: ScriptedPlayerDef = {
  name: "mission_yielder",
  create: () => makeMissionNav(null, true),
};

/** The runner that drives like Bulldog's operators: right side 0.3 m off the
 * right kerb (ADR-085). The teacher for self-play data. */
const missionRunnerKerb: ScriptedPlayerDef = {
  name: "mission_runner_kerb",
  create: () => makeMissionNav(null, false, { kerbGapM: 0.3 }),
};

/** Rams the LEFT curb of the second street just after the roundabout
 * (path progress r_out + 16, no agent within 2.5 m there) until one recovery
 * fires, then runs competently. */
const missionClumsy: ScriptedPlayerDef = {
  name: "mission_clumsy",
  create: () => {
    const nav = makeMissionNav(null, false);
    let ramUntil: number | null = null;
    return (view, t) => {
      const path = view.path;
      if (path && view.recoveryCount < 1) {
        const rOut = path.anchor("r_out");
        const s = view.progress.s;
        if (ramUntil === null && s > rOut + 16 && s < rOut + 19) ramUntil = t + 6;
        if (ramUntil !== null && t < ramUntil) {
          // Drive into the left curb. Under the racing stick (ADR-023) a
          // sideways push is a wheel, not a direction, so aim the robot at
          // the curb instead and hold the throttle down.
          if (view.controlScheme === "rate") {
            const here = path.point(Math.min(path.length, s));
            return steerToward(view, here.theta - 1.4, FULL);
          }
          return { dirX: -1, dirY: 0.05, mag: FULL };
        }
      }
      return nav(view, t);
    };
  },
};

/** Takes the WRONG branch at the first junction (turns left instead of
 * right), then recovers onto the route — exercises wrong-way guidance. */
const missionLost: ScriptedPlayerDef = {
  name: "mission_lost",
  create: () => {
    const nav = makeMissionNav(null, false);
    let lostUntil: number | null = null;
    let lostBearing = 0;
    return (view, t) => {
      const path = view.path;
      if (!path) return nav(view, t);
      const j1 = path.anchor("j1");
      const s = view.progress.s;
      const rate = view.controlScheme === "rate";
      // The heading stick can be aimed at the branch while still short of
      // the junction (the assist slides it along the alley wall); the wheel
      // has to wait until the mouth is actually there to turn into.
      const lostFrom = rate ? j1 - 0.5 : j1 - 4;
      if (lostUntil === null && s > lostFrom && s < j1 + 2 && view.guidance?.kind !== "turn_around") {
        // The racing stick has to turn the robot before it can go the wrong
        // way, so it needs longer on the wheel than one heading push.
        lostUntil = t + (rate ? 5.5 : 2.6);
        lostBearing = path.point(Math.min(path.length, s)).theta - Math.PI / 2;
      }
      if (lostUntil !== null && t < lostUntil) {
        // Hard left relative to the road: into the west branch.
        // Ease off for the turn — the wheel bites hardest at low speed —
        // then run down the wrong branch (ADR-023).
        if (rate) {
          const err = Math.abs(wrapAngle(lostBearing - view.robot.heading));
          const throttle = err < 0.4 ? FULL : view.robot.speed > 4.5 ? 0 : 0.45;
          return steerToward(view, lostBearing, throttle);
        }
        return { dirX: -1, dirY: 0.25, mag: FULL };
      }
      if (lostUntil !== null && t < lostUntil + 2.4) {
        // Now RUN down the wrong branch, biased a touch north: a dormant
        // motorbike waits mid-alley there, and the gap past its north side
        // is the lane the old trajectory happened to thread. Handing back
        // to the nav at the mouth let it recover before the wrong-way
        // latch fired — the whole point of this player is to trip it.
        return steerToward(view, lostBearing + 0.12, 0.75);
      }
      return nav(view, t);
    };
  },
};

export const SCRIPTED_PLAYERS: readonly ScriptedPlayerDef[] = [
  alwaysLeft,
  alwaysRight,
  alwaysYield,
  followComplementary,
  oscillate,
  randomPlayer,
];

/** Extra players for recovery testing/recordings; never in the AT-11 harness. */
export const DEV_PLAYERS: readonly ScriptedPlayerDef[] = [
  wallRam,
  wallRamTwice,
  missionRunner,
  missionRunnerKerb,
  missionLeft,
  missionRight,
  missionYielder,
  missionClumsy,
  missionLost,
];

export function getScriptedPlayer(name: string): ScriptedPlayerDef | null {
  return (
    SCRIPTED_PLAYERS.find((p) => p.name === name) ??
    DEV_PLAYERS.find((p) => p.name === name) ??
    null
  );
}
